import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { Eye, Download, MessageCircle, Printer, MoreHorizontal, X, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { formatLocal } from "@/lib/utils";
import { cleanOperatorName } from "@/lib/utils";
import { useBranding } from "@/context/BrandingContext";

async function fetchShare(topupId) {
  const { data } = await api.get(`/topups/${topupId}/receipt/share`);
  return data;
}

async function downloadPdf(topupId) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}/topups/${topupId}/receipt.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `receipt-${topupId.slice(0, 8).toUpperCase()}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TransactionRowActions({ topup }) {
  const [open, setOpen] = useState(false);
  const { brand } = useBranding();
  const brandName = brand.brand_name || "My GlobalTopup";
  const [busy, setBusy] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const canShare = topup.status === "SUCCESS";

  const recompute = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuW = 180;
    const left = Math.max(8, Math.min(window.innerWidth - menuW - 8, r.right - menuW));
    setPos({ top: r.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const fn = () => recompute();
    window.addEventListener("scroll", fn, true);
    window.addEventListener("resize", fn);
    return () => {
      window.removeEventListener("scroll", fn, true);
      window.removeEventListener("resize", fn);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const doDownload = async () => {
    setOpen(false);
    setBusy(true);
    try {
      await downloadPdf(topup.id);
      toast.success("Receipt downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const doWhatsApp = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const info = await fetchShare(topup.id);
      const url = `${API.replace(/\/api$/, "")}${info.public_path}`;
      const text = encodeURIComponent(`Your ${brandName} receipt #${info.receipt_no}\n${url}`);
      window.open(`https://wa.me/?text=${text}`, "_blank");
    } catch {
      toast.error("Share link unavailable");
    } finally {
      setBusy(false);
    }
  };

  const doPrint = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const info = await fetchShare(topup.id);
      const url = `${API.replace(/\/api$/, "")}${info.public_path}?print=1`;
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked");
    } catch {
      toast.error("Print failed");
    } finally {
      setBusy(false);
    }
  };

  if (!canShare) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid={`tx-actions-${topup.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        disabled={busy}
        className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0F62FE] hover:border-[#0F62FE] disabled:opacity-50 text-xs font-semibold"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MoreHorizontal className="w-3.5 h-3.5" />}
        Actions
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: 180, zIndex: 9999 }}
          className="rounded-lg border border-slate-200 bg-white shadow-xl py-1 text-sm animate-in fade-in-0 zoom-in-95 duration-100"
        >
          <MenuItem
            icon={Eye}
            label="View receipt"
            onClick={() => { setShowReceipt(true); setOpen(false); }}
            testid={`tx-view-${topup.id}`}
          />
          <MenuItem icon={Download} label="Download PDF" onClick={doDownload} testid={`tx-download-${topup.id}`} />
          <MenuItem icon={MessageCircle} label="Share WhatsApp" onClick={doWhatsApp} testid={`tx-whatsapp-${topup.id}`} />
          <MenuItem icon={Printer} label="Print" onClick={doPrint} testid={`tx-print-${topup.id}`} />
        </div>,
        document.body,
      )}
      {showReceipt && <ReceiptModal topup={topup} brandName={brandName} onClose={() => setShowReceipt(false)} />}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-slate-700 hover:text-[#0F62FE] text-left"
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function ReceiptModal({ topup, brandName, onClose }) {
  const localCcy = (topup.local_currency || "USD").toUpperCase();
  const paidLocal = topup.customer_pays_local != null
    ? topup.customer_pays_local
    : Number(topup.retail_price_usd || 0) * Number(topup.exchange_rate || 1);
  const ref = (topup.id || "").replace(/-/g, "").toUpperCase().slice(0, 12);
  const fmtTs = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div data-testid="tx-receipt-modal" className="relative bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="bg-[#0F62FE] text-white px-5 py-4 relative">
          <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-white/10 rounded-md">
            <X className="w-4 h-4" />
          </button>
          <div className="font-bold text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {brandName}
          </div>
          <div className="text-xs opacity-85">Topup Receipt · #{ref.slice(0, 8)}</div>
          <div className="mt-3 inline-block bg-emerald-500 text-white text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full">
            SUCCESS
          </div>
        </div>
        <div className="px-5 py-2 text-sm divide-y divide-slate-100">
          <Row k="Reference" v={ref} mono />
          <Row k="Date & time" v={fmtTs(topup.completed_at || topup.updated_at || topup.created_at)} />
          <Row k="Operator" v={cleanOperatorName(topup.operator_name)} />
          <Row k="Destination number" v={topup.phone_number} mono />
          {topup.receive_value > 0 && topup.receive_currency && (
            <Row
              k="Customer receives"
              v={`${Number(topup.receive_value).toFixed(2)} ${topup.receive_currency}`}
              emphasis
            />
          )}
        </div>
        <div className="px-5 py-4 bg-[#EDF5FF] flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Amount paid</div>
          <div className="text-2xl font-bold text-[#0F62FE]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {formatLocal(paidLocal, localCcy)}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 text-center text-xs text-slate-500">
          Thank you for using {brandName}.
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, mono, emphasis }) {
  return (
    <div className="py-2 flex items-center justify-between gap-2">
      <div className="text-slate-500 text-xs">{k}</div>
      <div className={`text-right ${mono ? "font-mono text-xs" : ""} ${emphasis ? "text-emerald-700 font-bold" : "text-slate-800 font-semibold"}`}>
        {v || "—"}
      </div>
    </div>
  );
}
